import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Copy, Sparkles, RefreshCw, Calendar, Bot, Loader2,
} from "lucide-react";
import { toast } from "sonner";

function copy(text, label) {
  navigator.clipboard?.writeText(text);
  toast.success(`${label} copied`);
}

const SECTION_META = {
  reddit: { label: "Reddit (r/electricians)", color: "bg-orange-50 border-orange-300 text-orange-700" },
  fb_group: { label: "Facebook Group (Fire Alarm Technicians)", color: "bg-blue-50 border-blue-300 text-blue-700" },
  linkedin: { label: "LinkedIn", color: "bg-sky-50 border-sky-300 text-sky-700" },
  short: { label: "Short / Reel / TikTok script", color: "bg-purple-50 border-purple-300 text-purple-700" },
  corporate_email: { label: "Corporate cold email (B2B)", color: "bg-emerald-50 border-emerald-300 text-emerald-700" },
};

export default function InstructorAIDrafts() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [{ data: t }, { data: h }] = await Promise.all([
        api.get("/admin/ai-drafts/today"),
        api.get("/admin/ai-drafts/history?limit=14"),
      ]);
      setToday(t);
      setHistory(h.history || []);
    } catch {
      toast.error("Couldn't load AI drafts");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generateNow = async () => {
    setGenerating(true);
    try {
      await api.post("/admin/ai-drafts/generate");
      toast.info("Claude is writing your drafts — ~45 sec");
      // Poll status every 3 sec until done
      const deadline = Date.now() + 180000; // 3 min max
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: status } = await api.get("/admin/ai-drafts/status");
        if (!status.running) {
          if (status.last_error) {
            toast.error(`Generation failed: ${status.last_error}`);
          } else {
            toast.success(`Fresh drafts ready for ${status.last_date_completed}`);
          }
          break;
        }
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generation failed";
      toast.error(typeof msg === "string" ? msg : "Generation failed");
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading…</div></div>;

  const bundle = today?.bundle;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="ai-drafts-page">
        <Link to="/instructor/launch" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-red-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Mission control
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="overline mb-2"><span className="red-bar" />AI content drafter</div>
            <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter mb-2">Today's drafts</h1>
            <p className="text-sm text-zinc-700 max-w-2xl leading-relaxed">
              Claude (same AI that powers your Tutor) writes fresh marketing content every morning at 7 AM ET.
              Reddit post, FB group post, LinkedIn post, Short script + cold email — all in your voice. Tap Copy → paste → post.
            </p>
          </div>
          <button onClick={generateNow} disabled={generating} className="btn-primary text-xs" data-testid="generate-now">
            {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</> : <><Sparkles className="w-3.5 h-3.5" /> Generate fresh drafts</>}
          </button>
        </div>

        {generating && (
          <div className="card-tech p-4 mb-6 text-sm text-zinc-700" data-testid="generating-banner">
            <Loader2 className="inline w-4 h-4 animate-spin mr-2 text-red-600" />
            Claude is writing your drafts — usually 30-45 seconds. Don't refresh.
          </div>
        )}

        {!bundle ? (
          <div className="card-tech p-8 text-center" data-testid="empty-state">
            <Bot className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-bold mb-1">No drafts for today yet</h3>
            <p className="text-sm text-zinc-600 mb-4">
              The daily scheduler runs at 7 AM ET. Click <strong>Generate fresh drafts</strong> above to create today's now.
            </p>
          </div>
        ) : (
          <>
            {/* Topic banner */}
            <div className="card-tech p-4 mb-6" data-testid="today-topic">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-red-600" />
                <div className="overline">Today's angle ({bundle.date})</div>
              </div>
              <div className="text-sm font-semibold leading-snug">"{bundle.topic_today}"</div>
              <div className="text-[11px] text-zinc-500 mt-2 font-mono">
                Short script angle: {bundle.topic_short}<br />
                Cold email angle: {bundle.topic_email}
              </div>
            </div>

            {/* Drafts grid */}
            <div className="space-y-4" data-testid="drafts-list">
              {Object.entries(bundle.drafts || {}).map(([key, draft]) => {
                const meta = SECTION_META[key] || { label: key, color: "bg-zinc-50 border-zinc-300 text-zinc-700" };
                return (
                  <div key={key} className="card-tech p-5" data-testid={`draft-${key}`}>
                    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${meta.color} mb-3`}>
                      {meta.label}
                    </span>
                    <pre className="text-xs sm:text-sm text-zinc-800 whitespace-pre-wrap bg-zinc-50 p-3 mb-3 max-h-96 overflow-y-auto border border-zinc-100 leading-relaxed">
                      {draft.content}
                    </pre>
                    <button onClick={() => copy(draft.content, meta.label)} className="btn-primary text-xs" data-testid={`copy-${key}`}>
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* History */}
        {history.length > 1 && (
          <details className="card-tech p-5 mt-8" data-testid="drafts-history">
            <summary className="font-heading font-bold cursor-pointer text-sm">Past 14 days of drafts ({history.length})</summary>
            <ul className="mt-3 divide-y divide-zinc-100 text-sm">
              {history.map((h) => (
                <li key={h.date} className="py-2 flex justify-between gap-3" data-testid={`history-${h.date}`}>
                  <div className="font-mono">{h.date}</div>
                  <div className="text-zinc-600 text-xs truncate flex-1 text-right">"{h.topic_today}"</div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-zinc-500 mt-3">Re-generate today's drafts to overwrite. Past days are read-only.</p>
          </details>
        )}
      </main>
    </div>
  );
}
